import React from "react";
import {
  Alert,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CostExplainer from "../../../common/cost-explainer";
import { formatCurrency, formatNumber } from "./validation";
import { AI_COST_SHOW_INR } from "../../../utils/currency";

const BREAKDOWN_ROWS = [
  ["chat", "Chat (answers and query rewrites)"],
  ["embedding", "Embeddings (knowledge-base training and question lookup)"],
];

/**
 * How the AI Bot's cost splits up: chat vs embeddings, BotBridge keys vs the tenant's
 * own keys, and per model. Costs are recorded per call at the model's price that day.
 */
const CostDetails = ({ data, currency, rate }) => {
  if (!data) return null;
  const money = (value) => formatCurrency(value, currency, rate);
  const breakdown = data.cost_breakdown || {};
  const byModel = data.cost_by_model || [];

  return (
    <Box sx={{ mt: 4 }}>
      {data.unpriced_requests > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {formatNumber(data.unpriced_requests)} AI call(s) in this period were on a model with no known price yet, so
          they are not included in the cost.
        </Alert>
      )}
      {data.cost_legacy_days > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.cost_legacy_days} day(s) in this period are from before per-model cost recording, and are estimated at
          Gemini 2.5 Flash-Lite prices (the model every AI Bot used then).
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Cost Breakdown
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell align="right">BotBridge keys</TableCell>
                    <TableCell align="right">Your own keys</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {BREAKDOWN_ROWS.map(([kind, label]) => (
                    <TableRow key={kind}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">{money(breakdown[kind]?.platform)}</TableCell>
                      <TableCell align="right">{money(breakdown[kind]?.own)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Calls on your own keys are billed to you by the provider directly.
              {data.cost_estimated_share > 0 &&
                ` ${(data.cost_estimated_share * 100).toFixed(1)}% of this cost is estimated from text length, because the provider reports no token counts for it.`}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Cost by Model
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Input tokens</TableCell>
                    <TableCell align="right">Output tokens</TableCell>
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byModel.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>
                        No AI usage in this period
                      </TableCell>
                    </TableRow>
                  )}
                  {byModel.map((row) => (
                    <TableRow key={`${row.provider}/${row.model}/${row.display_name}`}>
                      <TableCell>{row.display_name}</TableCell>
                      <TableCell align="right">{formatNumber(row.calls)}</TableCell>
                      <TableCell align="right">{formatNumber(row.input_tokens)}</TableCell>
                      <TableCell align="right">{formatNumber(row.output_tokens)}</TableCell>
                      <TableCell align="right">{money(row.cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <CostExplainer
        title="How costs are calculated"
        priceSource="Each AI call is costed when it happens, at its model's published list price that day. Prices are refreshed at least daily from a public price list (LiteLLM's model price file), because the AI providers' APIs don't publish prices."
        pricesLastUpdated={data.prices_last_updated}
        formula="Cost of a call = input tokens × input price per 1M + output tokens × output price per 1M. Totals add up the recorded cost of every call in the period; a later price change never changes a past day."
        steps={[
          "Chat calls: rewriting the customer's question and writing the answer.",
          "Embeddings: training the knowledge base, and looking up each question in it.",
          "Monthly projection: the average daily cost in this period × 30.",
          "Days before per-model cost recording are estimated at Gemini 2.5 Flash-Lite prices.",
        ]}
        usdToInrRate={AI_COST_SHOW_INR ? rate : undefined}
        exclusions={[
          "Taxes, and any discounts or credits from the provider.",
          "Voice (speech-to-text and text-to-speech).",
        ]}
      />
    </Box>
  );
};

export default CostDetails;
